import React, { Component } from 'react';
import magicLinkService from 'services/magicLinkService';
import googleAnalyticsUtils, { SIGN_IN_EVENTS } from 'utils/googleAnalytics';
import CheckEmail from 'components/SignIn/CheckEmail';
import {
  isThrottled,
  magicLinkRequestErrorMessage,
  rememberMagicLinkContext,
  retryAfterSeconds,
  MAGIC_LINK_DEFAULT_RETRY_SECONDS,
  MAGIC_LINK_RESEND_SECONDS,
} from 'utils/magicLink';
import 'components/SignIn/SignIn.scss';

export const VIEWS = {
  EMAIL: 'email',
  CHECKING: 'checking',
  LINK: 'link',
  PASSWORD: 'password',
  SENT: 'sent',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), [tabindex="0"]';

// The whole web sign-in in one dialog (full-screen sheet on phones).
//
// 1. Email first: pre-login decides between sign-in and signup.
// 2. Registered email: if POST /user/v1/login/magic-link-status says links are enabled,
//    "Email me a sign-in link" is the main button and "Use my password instead" opens
//    the password form. Otherwise it is the password form as before. "enforced" is
//    ignored on purpose: password sign-in is always one tap away.
// 3. After sending: "Check your email" with a 30-second resend countdown.
export class SignInPanel extends Component {
  static defaultProps = {
    statusTimeoutMs: 5000,
  };

  constructor(props) {
    super(props);
    this.state = {
      emailInput: '',
      submittingEmail: false,
      password: '',
      staySigned: false,
      loggingIn: false,
      linkStatus: 'idle', // idle | checking | enabled | disabled
      choice: null, // null (default for the status) | 'link' | 'password' | 'sent'
      sending: false,
      message: '',
      messageIsError: false,
      cooldownSeconds: MAGIC_LINK_RESEND_SECONDS,
      cooldownKey: 0,
    };
  }

  componentDidMount() {
    if (this.props.preLogin && this.props.email) {
      this.loadStatus(this.props.email);
    }
    if (this.props.open) {
      this.onOpened();
    }
  }

  componentDidUpdate(previousProps, previousState) {
    const emailChanged =
      previousProps.email !== this.props.email || previousProps.preLogin !== this.props.preLogin;
    if (emailChanged) {
      this.resetAccountStep();
      if (this.props.preLogin && this.props.email) {
        this.loadStatus(this.props.email);
      }
    }
    if (!previousProps.open && this.props.open) {
      this.onOpened();
    }
    if (previousProps.open && !this.props.open) {
      this.onClosed();
    }
    if (this.props.open && this.view(previousProps, previousState) !== this.view()) {
      this.focusView();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.statusTimer);
    this.onClosed();
  }

  service() {
    return this.props.service || magicLinkService;
  }

  track(action, label) {
    const analytics = this.props.analytics || googleAnalyticsUtils;
    analytics.sendSignInEvent(action, label);
  }

  safeSetState(update) {
    if (!this.unmounted) {
      this.setState(update);
    }
  }

  // ---- Dialog behaviour -------------------------------------------------------------

  onOpened() {
    this.returnFocusTo = typeof document !== 'undefined' ? document.activeElement : null;
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.add('sign-in-open');
      document.addEventListener('keydown', this.handleDocumentKeyDown);
    }
    this.focusView();
  }

  onClosed() {
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('sign-in-open');
      document.removeEventListener('keydown', this.handleDocumentKeyDown);
    }
    if (this.returnFocusTo && typeof this.returnFocusTo.focus === 'function') {
      this.returnFocusTo.focus();
    }
    this.returnFocusTo = null;
  }

  handleDocumentKeyDown = event => {
    if (event.key === 'Escape' || event.keyCode === 27) {
      this.props.onClose && this.props.onClose();
    }
  };

  // Keep Tab inside the dialog.
  handleDialogKeyDown = event => {
    if (event.key !== 'Tab' || !this.dialog) {
      return;
    }
    const nodes = Array.prototype.slice.call(this.dialog.querySelectorAll(FOCUSABLE));
    if (!nodes.length) {
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  handleOverlayClick = event => {
    if (event.target === event.currentTarget) {
      this.props.onClose && this.props.onClose();
    }
  };

  focusView() {
    // After the DOM for the new step exists.
    setTimeout(() => {
      if (this.unmounted || !this.dialog) {
        return;
      }
      const target =
        this.dialog.querySelector('[data-autofocus]') || this.dialog.querySelector('h2');
      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    }, 20);
  }

  // ---- State ------------------------------------------------------------------------

  view(props = this.props, state = this.state) {
    if (!props.preLogin || !props.email) {
      return VIEWS.EMAIL;
    }
    if (state.choice === 'sent') {
      return VIEWS.SENT;
    }
    if (state.choice === 'password') {
      return VIEWS.PASSWORD;
    }
    if (state.linkStatus === 'checking' || state.linkStatus === 'idle') {
      return VIEWS.CHECKING;
    }
    if (state.linkStatus === 'enabled') {
      return VIEWS.LINK;
    }
    return VIEWS.PASSWORD;
  }

  resetAccountStep() {
    clearTimeout(this.statusTimer);
    this.setState({
      linkStatus: 'idle',
      choice: null,
      sending: false,
      password: '',
      loggingIn: false,
      message: '',
      messageIsError: false,
    });
  }

  loadStatus(email) {
    const trimmed = (email || '').trim();
    if (!trimmed) {
      return undefined;
    }
    this.setState({ linkStatus: 'checking' });
    clearTimeout(this.statusTimer);
    // A slow status call must never block sign-in: fall back to the password form.
    this.statusTimer = setTimeout(() => {
      if (!this.unmounted && this.state.linkStatus === 'checking' && this.props.email === email) {
        this.setState({ linkStatus: 'disabled' });
      }
    }, this.props.statusTimeoutMs);

    return this.service()
      .status(trimmed)
      .then(
        result => !!(result && result.enabled),
        () => false
      )
      .then(enabled => {
        if (this.unmounted || this.props.email !== email || this.state.linkStatus !== 'checking') {
          return;
        }
        clearTimeout(this.statusTimer);
        this.setState({ linkStatus: enabled ? 'enabled' : 'disabled' });
      });
  }

  // ---- Handlers ---------------------------------------------------------------------

  updateField = ({ target }) => {
    this.setState({ [target.name]: target.type === 'checkbox' ? target.checked : target.value });
  };

  handleSubmitEmail = event => {
    event && event.preventDefault && event.preventDefault();
    const email = this.state.emailInput.trim();
    if (!email || this.state.submittingEmail) {
      return undefined;
    }
    this.setState({ submittingEmail: true });
    const done = () => this.safeSetState({ submittingEmail: false });
    return Promise.resolve(this.props.onSubmitEmail && this.props.onSubmitEmail(email)).then(done, done);
  };

  handleLogin = event => {
    event && event.preventDefault && event.preventDefault();
    if (this.state.loggingIn || !this.state.password) {
      return undefined;
    }
    this.setState({ loggingIn: true });
    const done = () => this.safeSetState({ loggingIn: false });
    return Promise.resolve(
      this.props.onLogin &&
        this.props.onLogin({ password: this.state.password, staySigned: this.state.staySigned })
    ).then(done, done);
  };

  handleUsePassword = () => {
    this.track(SIGN_IN_EVENTS.PASSWORD_FALLBACK_CLICKED, this.view());
    this.setState({ choice: 'password', message: '', messageIsError: false });
  };

  handleUseLink = () => {
    this.setState({ choice: 'link', message: '', messageIsError: false });
  };

  handleChangeEmail = () => {
    this.setState({ emailInput: this.props.email || this.state.emailInput });
    this.props.onUseDifferentEmail && this.props.onUseDifferentEmail();
  };

  handleRequestLink = () => {
    const email = (this.props.email || '').trim();
    if (!email || this.state.sending) {
      return undefined;
    }
    const resend = this.state.choice === 'sent';
    this.track(SIGN_IN_EVENTS.MAGIC_LINK_REQUESTED, resend ? 'resend' : 'first');
    rememberMagicLinkContext({ email, redirect: this.props.redirect });
    this.setState({ sending: true, message: '', messageIsError: false });

    return this.service()
      .request(email)
      .then(() => {
        this.safeSetState(state => ({
          sending: false,
          choice: 'sent',
          message: resend ? 'We sent you a new link. Use the newest email.' : '',
          messageIsError: false,
          cooldownSeconds: MAGIC_LINK_RESEND_SECONDS,
          cooldownKey: state.cooldownKey + 1,
        }));
      })
      .catch(error => {
        const throttled = isThrottled(error);
        this.safeSetState(state => ({
          sending: false,
          message: magicLinkRequestErrorMessage(error),
          messageIsError: true,
          cooldownSeconds: throttled
            ? retryAfterSeconds(error) || MAGIC_LINK_DEFAULT_RETRY_SECONDS
            : state.cooldownSeconds,
          cooldownKey: throttled && resend ? state.cooldownKey + 1 : state.cooldownKey,
        }));
      });
  };

  // ---- Rendering --------------------------------------------------------------------

  renderMessage() {
    const { message, messageIsError } = this.state;
    if (!message) {
      return null;
    }
    return (
      <p
        className={messageIsError ? 'sign-in__message sign-in__message--error' : 'sign-in__message'}
        role={messageIsError ? 'alert' : 'status'}
      >
        {message}
      </p>
    );
  }

  renderAccount() {
    return (
      <div className="sign-in__account">
        <span className="sign-in__account-email">{this.props.email}</span>
        <button
          type="button"
          className="sign-in-link sign-in__change-email"
          onClick={this.handleChangeEmail}
          aria-label="Use a different email"
        >
          Change
        </button>
      </div>
    );
  }

  renderEmailStep() {
    const { emailInput, submittingEmail } = this.state;
    return (
      <form className="sign-in__email-step" onSubmit={this.handleSubmitEmail} noValidate>
        <h2 id="sign-in-title" className="sign-in__title" tabIndex={-1}>
          Sign in
        </h2>
        <p className="sign-in__intro">
          Enter your work email. New to Lunch.Team? We’ll help you create an account.
        </p>
        <label className="sign-in__label" htmlFor="sign-in-email">
          Work email
        </label>
        <input
          id="sign-in-email"
          className="sign-in__input"
          name="emailInput"
          type="email"
          inputMode="email"
          autoComplete="username email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="name@company.com"
          value={emailInput}
          onChange={this.updateField}
          data-autofocus
        />
        <button
          type="submit"
          className="sign-in-btn sign-in-btn--primary sign-in__next"
          disabled={submittingEmail || !emailInput.trim()}
        >
          {submittingEmail ? 'Checking…' : 'Next'}
        </button>
      </form>
    );
  }

  renderChecking() {
    return (
      <div className="sign-in__checking">
        <h2 id="sign-in-title" className="sign-in__title" tabIndex={-1}>
          Welcome back
        </h2>
        {this.renderAccount()}
        <p className="sign-in__message" role="status">
          One moment…
        </p>
        <div className="sign-in__links">
          <button type="button" className="sign-in-link sign-in__use-password" onClick={this.handleUsePassword}>
            Use my password instead
          </button>
        </div>
      </div>
    );
  }

  renderLinkStep() {
    const { sending } = this.state;
    return (
      <div className="sign-in__link-step">
        <h2 id="sign-in-title" className="sign-in__title" tabIndex={-1}>
          Welcome back
        </h2>
        {this.renderAccount()}
        <p className="sign-in__intro">
          We’ll email you a link that signs you in. No password needed.
        </p>
        <button
          type="button"
          className="sign-in-btn sign-in-btn--primary sign-in__send-link"
          onClick={this.handleRequestLink}
          disabled={sending}
          data-autofocus
        >
          {sending ? 'Sending…' : 'Email me a sign-in link'}
        </button>
        {this.renderMessage()}
        <div className="sign-in__links">
          <button type="button" className="sign-in-link sign-in__use-password" onClick={this.handleUsePassword}>
            Use my password instead
          </button>
        </div>
      </div>
    );
  }

  renderPasswordStep() {
    const { password, staySigned, loggingIn, linkStatus } = this.state;
    return (
      <form className="sign-in__password-step" onSubmit={this.handleLogin} aria-label="Sign in with password">
        <h2 id="sign-in-title" className="sign-in__title" tabIndex={-1}>
          Welcome back
        </h2>
        {this.renderAccount()}
        {/* Lets password managers pair the saved password with this email. */}
        <input
          className="sign-in-visually-hidden"
          type="email"
          name="username"
          autoComplete="username"
          value={this.props.email || ''}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
        />
        <label className="sign-in__label" htmlFor="sign-in-password">
          Password
        </label>
        <input
          id="sign-in-password"
          className="sign-in__input"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={this.updateField}
          data-autofocus
        />
        <label className="sign-in__checkbox" htmlFor="sign-in-stay">
          <input
            id="sign-in-stay"
            name="staySigned"
            type="checkbox"
            checked={staySigned}
            onChange={this.updateField}
          />
          Stay signed in
        </label>
        <button
          type="submit"
          className="sign-in-btn sign-in-btn--primary sign-in__login"
          disabled={loggingIn || !password}
        >
          {loggingIn ? 'Signing in…' : 'Log in'}
        </button>
        {this.renderMessage()}
        <div className="sign-in__links">
          <button type="button" className="sign-in-link sign-in__forgot" onClick={this.props.onForgotPassword}>
            Forgot your password?
          </button>
          {linkStatus === 'enabled' && (
            <button type="button" className="sign-in-link sign-in__use-link" onClick={this.handleUseLink}>
              Email me a sign-in link instead
            </button>
          )}
        </div>
      </form>
    );
  }

  renderSent() {
    return (
      <CheckEmail
        email={this.props.email}
        onResend={this.handleRequestLink}
        resending={this.state.sending}
        cooldownSeconds={this.state.cooldownSeconds}
        cooldownKey={this.state.cooldownKey}
        message={this.state.message}
        messageIsError={this.state.messageIsError}
        onUsePassword={this.handleUsePassword}
      />
    );
  }

  renderView() {
    switch (this.view()) {
      case VIEWS.EMAIL:
        return this.renderEmailStep();
      case VIEWS.CHECKING:
        return this.renderChecking();
      case VIEWS.LINK:
        return this.renderLinkStep();
      case VIEWS.SENT:
        return this.renderSent();
      default:
        return this.renderPasswordStep();
    }
  }

  render() {
    if (!this.props.open) {
      return null;
    }
    return (
      <div className="sign-in-overlay" onClick={this.handleOverlayClick}>
        <div
          className="sign-in-dialog sign-in"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sign-in-title"
          onKeyDown={this.handleDialogKeyDown}
          ref={node => {
            this.dialog = node;
          }}
        >
          <button
            type="button"
            className="sign-in-dialog__close"
            onClick={this.props.onClose}
            aria-label="Close sign in"
          >
            ×
          </button>
          {this.renderView()}
        </div>
      </div>
    );
  }
}

export default SignInPanel;



// WEBPACK FOOTER //
// ./src/containers/Landing/Header/SignInPanel.js